import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox, FormControlLabel, Modal, TextField } from '@mui/material';

import {
  createPblCitizenAuthorization,
  getPblOcContext,
  issuePblCitizenOtp,
  uploadPblFile,
  verifyPblCitizenOtp,
} from '@/api/pbl.api';
import TermsModal from '@/components/Account/RegisterForm/TermsModal';
import { Button } from '@/components/UI/Button';
import { SignatureModal } from '@/components/UI/Modal';
import { Text } from '@/components/UI/Typography/Text';
import useResize from '@/hooks/useResize';
import type { PblCitizenAuthorization, PblOcContext } from '@/types/pbl';
import { formatPblDisplayDate } from '@/utils/pblDate';
import { errorHandler } from '@/utils/toastHandler';

import { createPblUploadFormData, getUploadedPblFileId } from '../VolunteerRegistration/uploadedDocument';
import {
  applyPblOcContextToAuthorizationForm,
  buildOcCitizenAuthorizationPayload,
  initialOcCitizenAuthorizationForm,
  isOcCitizenAuthorizationComplete,
  OC_CITIZEN_TEST_STATION_OTP,
  OcCitizenAuthorizationForm,
  OcCitizenSignatureRole,
  signOcCitizenAuthorization,
} from './formState';
import {
  ActionRow,
  AuthorizationModalAction,
  AuthorizationModalSurface,
  AuthorizationModalText,
  AuthorizationModalTitle,
  ConsentText,
  ConsentList,
  DateText,
  EmailFieldRow,
  EmailOtpActionRow,
  FieldStack,
  FormCard,
  InfoGrid,
  InfoLabel,
  InfoValue,
  ModalAction,
  ModalSurface,
  ModalText,
  ModalTitle,
  OtpControlGroup,
  OtpFieldRow,
  OtpStatusText,
  PageShell,
  SectionTitle,
  SignatureBlock,
  SignatureItem,
  SignaturePreview,
  SignaturePreviewAction,
  SignaturePreviewImage,
  SignatureRow,
  SignedBadge,
} from './styles';

type TextFieldName =
  | 'citizen_surname_en'
  | 'citizen_given_name_en'
  | 'citizen_surname_zh'
  | 'citizen_given_name_zh'
  | 'identity_partial'
  | 'phone'
  | 'email'
  | 'residential_address';

type TermsModalOpenState = {
  infoCollect: boolean;
  terms: boolean;
};

type ReadOnlyConsentName = keyof TermsModalOpenState | 'authorization';
type ConsentAgreementField = 'pics_agreed' | 'terms_agreed' | 'authorisation_agreed';

type OtpChannel = 'phone' | 'email';

type OtpStatus = {
  tone: 'info' | 'success' | 'error';
  message: string;
};

type AccountSetupNotificationState = {
  notice: string | null;
  showSentEmailInstruction: boolean;
  followUpInstruction: string;
};

type ReadOnlyConsentLabel = {
  name: ReadOnlyConsentName;
  field: ConsentAgreementField;
  linkKey: string;
  linkDefault: string;
  suffixKey?: string;
  suffixDefault?: string;
};

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const nonProductionCitizenTestPassword = '!QAZse432';
const OTP_REQUEST_COOLDOWN_SECONDS = 60;

const getDefaultAccountSetupNotification = (
  tr: (key: string, defaultValue: string, options?: Record<string, unknown>) => string
): AccountSetupNotificationState => ({
  notice: null,
  showSentEmailInstruction: true,
  followUpInstruction: tr(
    'pbl.citizenAuthorizationCaseCreate.accountRegistration.successModal.followUpInstruction',
    'After setup, the citizen can log in to use the follow-up Pro Bono legal service functions.'
  ),
});

const getOtpLabel = (channel: OtpChannel, tr: (key: string, defaultValue: string) => string) =>
  channel === 'phone'
    ? tr('pbl.lawyerRegistration.otp.smsOtp', 'SMS OTP')
    : tr('pbl.lawyerRegistration.otp.emailOtp', 'Email OTP');
const getOtpFieldName = (channel: OtpChannel) => (channel === 'phone' ? 'phone_otp' : 'email_otp');

const readOnlyConsentLabels: ReadOnlyConsentLabel[] = [
  {
    name: 'infoCollect',
    field: 'pics_agreed',
    linkKey: 'pbl.citizenAuthorizationCaseCreate.accountRegistration.declarationConsent.personalInformationCollectionStatement',
    linkDefault: 'Personal Information Collection Statement',
    },
  {
    name: 'terms',
    field: 'terms_agreed',
    linkKey: 'pbl.citizenAuthorizationCaseCreate.accountRegistration.declarationConsent.termsAndConditions',
    linkDefault: 'Term and Conditions',
  },
  {
    name: 'authorization',
    field: 'authorisation_agreed',
    linkKey: 'pbl.citizenAuthorizationCaseCreate.accountRegistration.declarationConsent.authorization',
    linkDefault: 'Authorization',
  },
];

const consentFieldByTermsModalKey: Record<keyof TermsModalOpenState, ConsentAgreementField> = {
  infoCollect: 'pics_agreed',
  terms: 'terms_agreed',
};

const dataUrlToFile = (dataUrl: string, filename: string) => {
  const [metadata, base64Data] = dataUrl.split(',');
  const mimeType = metadata.match(/data:(.*);base64/)?.[1] || 'image/png';
  const binary = window.atob(base64Data);
  const bytes = new Uint8Array(binary.length);

  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }

  return new File([bytes], filename, { type: mimeType });
};

const formatSignatureDate = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}/${month}/${day}`;
};

const normalizeHongKongMobile = (value: string) => {
  const compactValue = value.replace(/[\s-]/g, '').trim();
  let phone = compactValue;

  if (phone.startsWith('+852')) {
    phone = phone.slice(4);
  } else if (phone.startsWith('852') && phone.length === 11) {
    phone = phone.slice(3);
  }

  if (!/^[4-9]\d{7}$/.test(phone)) {
    return null;
  }

  return {
    phone_country_code: '+852' as const,
    phone,
  };
};

const getApiErrorMessage = (error: any, fallback: string) =>
  error.response?.data?.message || error.response?.data?.data || error.message || fallback;

const getOtpCooldownSeconds = (cooldownSeconds?: number | null) => {
  const seconds = Number(cooldownSeconds);
  return Number.isFinite(seconds) && seconds > 0 ? Math.ceil(seconds) : OTP_REQUEST_COOLDOWN_SECONDS;
};

const getAccountSetupNotificationState = (
  authorization: PblCitizenAuthorization | undefined,
  tr: (key: string, defaultValue: string, options?: Record<string, unknown>) => string
): AccountSetupNotificationState => {
  const profile = authorization?.citizen_profile;
  const payload = authorization?.passwordless_payload;
  const isShadowCitizen = profile?.is_shadow_email === true || profile?.email === null;
  const defaultAccountSetupNotification = getDefaultAccountSetupNotification(tr);

  if (isShadowCitizen) {
    const smsSuppressed = payload?.sms_suppressed === true;
    const smsError = typeof payload?.sms_error === 'string' && payload.sms_error.trim();
    let notice: string | null = null;

    if (smsError) {
      notice = tr(
        'pbl.citizenAuthorizationCaseCreate.accountRegistration.successModal.notices.smsCouldNotSend',
        'The citizen account was created, but the SMS login link could not be sent. Please follow up with support if the citizen cannot receive the setup message.'
      );
    } else if (smsSuppressed) {
      notice = tr(
        'pbl.citizenAuthorizationCaseCreate.accountRegistration.successModal.notices.smsSuppressed',
        'This test environment did not send the SMS login link. The citizen account has still been created successfully.'
      );
    }

    return {
      notice,
      showSentEmailInstruction: false,
      followUpInstruction: tr(
        'pbl.citizenAuthorizationCaseCreate.accountRegistration.successModal.shadowCitizenInstruction',
        'The citizen should use their mobile number and HKID/Passport last 4 digits to request an SMS login link.'
      ),
    };
  }

  if (authorization?.citizen_profile?.password_setup_required === false) {
    return {
      notice: tr(
        'pbl.citizenAuthorizationCaseCreate.accountRegistration.successModal.testPasswordInstruction',
        'In this test environment, the citizen can sign in with their email and the test password {{password}}. No setup link is required.',
        { password: nonProductionCitizenTestPassword }
      ),
      showSentEmailInstruction: false,
      followUpInstruction: tr(
        'pbl.citizenAuthorizationCaseCreate.accountRegistration.successModal.loginInstruction',
        'The citizen can now log in and create their Pro Bono Legal Service case.'
      ),
    };
  }

  const emailSuppressed = payload?.email_suppressed === true;
  const smsSuppressed = payload?.sms_suppressed === true;
  const emailError = typeof payload?.email_error === 'string' && payload.email_error.trim();
  const smsError = typeof payload?.sms_error === 'string' && payload.sms_error.trim();

  if (emailSuppressed && smsSuppressed) {
    return {
      notice: tr(
        'pbl.citizenAuthorizationCaseCreate.accountRegistration.successModal.notices.emailSmsSuppressed',
        'This test environment did not send account setup email or SMS. The citizen account has still been created successfully.'
      ),
      showSentEmailInstruction: false,
      followUpInstruction: defaultAccountSetupNotification.followUpInstruction,
    };
  }

  if (emailSuppressed) {
    return {
      notice: tr(
        'pbl.citizenAuthorizationCaseCreate.accountRegistration.successModal.notices.emailSuppressed',
        'This test environment did not send the account setup email. The citizen account has still been created successfully.'
      ),
      showSentEmailInstruction: false,
      followUpInstruction: defaultAccountSetupNotification.followUpInstruction,
    };
  }

  if (smsSuppressed) {
    return {
      notice: tr(
        'pbl.citizenAuthorizationCaseCreate.accountRegistration.successModal.notices.setupSmsSuppressed',
        'This test environment did not send the account setup SMS. The citizen account has still been created successfully.'
      ),
      showSentEmailInstruction: true,
      followUpInstruction: defaultAccountSetupNotification.followUpInstruction,
    };
  }

  if (emailError || smsError) {
    return {
      notice: tr(
        'pbl.citizenAuthorizationCaseCreate.accountRegistration.successModal.notices.notificationError',
        'The citizen account was created, but one or more account setup notifications could not be sent. Please follow up with support if the citizen does not receive the setup message.'
      ),
      showSentEmailInstruction: !emailError,
      followUpInstruction: defaultAccountSetupNotification.followUpInstruction,
    };
  }

  return defaultAccountSetupNotification;
};

const OcCitizenRegistration = () => {
  // @ts-ignore
  const { t } = useTranslation();
  const tr = (key: string, defaultValue: string, options?: Record<string, unknown>) =>
    t(key as any, { defaultValue, ...options }) as string;
  const { isMobile, isTablet } = useResize();
  const [form, setForm] = useState<OcCitizenAuthorizationForm>(initialOcCitizenAuthorizationForm);
  const [authorizationOpen, setAuthorizationOpen] = useState(false);
  const [termsOpen, setTermsOpen] = useState<TermsModalOpenState>({
    infoCollect: false,
    terms: false,
  });
  const [successOpen, setSuccessOpen] = useState(false);
  const [accountSetupNotification, setAccountSetupNotification] = useState(() =>
    getDefaultAccountSetupNotification(tr)
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [ocContext, setOcContext] = useState<PblOcContext | null>(null);
  const [activeSignatureRole, setActiveSignatureRole] = useState<OcCitizenSignatureRole | null>(null);
  const [otpIssued, setOtpIssued] = useState<Record<OtpChannel, boolean>>({
    phone: false,
    email: false,
  });
  const [otpStatus, setOtpStatus] = useState<Record<OtpChannel, OtpStatus | null>>({
    phone: null,
    email: null,
  });
  const [issuingOtp, setIssuingOtp] = useState<OtpChannel | null>(null);
  const [verifyingOtp, setVerifyingOtp] = useState<OtpChannel | null>(null);
  const [otpCooldownSeconds, setOtpCooldownSeconds] = useState<Record<OtpChannel, number>>({
    phone: 0,
    email: 0,
  });

  const isComplete = useMemo(() => isOcCitizenAuthorizationComplete(form), [form]);
  const phoneOtpCooldownActive = otpCooldownSeconds.phone > 0;
  const emailOtpCooldownActive = otpCooldownSeconds.email > 0;

  useEffect(() => {
    let isMounted = true;

    const loadOcContext = async () => {
      try {
        const response = await getPblOcContext();

        if (!isMounted) return;

        if (response.data.success === false) {
          throw new Error(response.data.message || 'Failed to load district office information.');
        }

        setOcContext(response.data.data);
        setForm((prev) => applyPblOcContextToAuthorizationForm(prev, response.data.data));
      } catch (error: any) {
        if (isMounted) {
          errorHandler(
              error.response?.data?.message ||
              error.response?.data?.data ||
              error.message ||
              tr(
                'pbl.citizenAuthorizationCaseCreate.shared.messages.loadOfficeFailed',
                'Failed to load district office information.'
              )
          );
        }
      }
    };

    loadOcContext();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!phoneOtpCooldownActive && !emailOtpCooldownActive) {
      return undefined;
    }

    const timerId = window.setInterval(() => {
      setOtpCooldownSeconds((prev) => ({
        phone: Math.max(prev.phone - 1, 0),
        email: Math.max(prev.email - 1, 0),
      }));
    }, 1000);

    return () => window.clearInterval(timerId);
  }, [emailOtpCooldownActive, phoneOtpCooldownActive]);

  const setChannelStatus = (channel: OtpChannel, status: OtpStatus | null) => {
    setOtpStatus((prev) => ({ ...prev, [channel]: status }));
  };

  const isOtpRequestCoolingDown = (channel: OtpChannel) => otpCooldownSeconds[channel] > 0;

  const startOtpRequestCooldown = (channel: OtpChannel, cooldownSeconds?: number | null) => {
    setOtpCooldownSeconds((prev) => ({
      ...prev,
      [channel]: getOtpCooldownSeconds(cooldownSeconds),
    }));
  };

  const getOtpRequestButtonLabel = (channel: OtpChannel) => {
    if (issuingOtp === channel) {
      return tr('pbl.citizenAuthorizationCaseCreate.shared.otp.sending', 'Sending...');
    }

    const cooldownSeconds = otpCooldownSeconds[channel];

    if (cooldownSeconds > 0) {
      return tr(
        'pbl.citizenAuthorizationCaseCreate.shared.otp.verifyCooldown',
        `Verify (${cooldownSeconds}s)`,
        { seconds: cooldownSeconds }
      );
    }

    return tr('pbl.citizenAuthorizationCaseCreate.shared.otp.verify', 'OTP verify');
  };

  const updateOtpValue = (channel: OtpChannel, value: string) => {
    setForm((prev) => ({
      ...prev,
      [getOtpFieldName(channel)]: value,
    }));
  };

  const updateOtpVerified = (channel: OtpChannel, value: boolean) => {
    setForm((prev) => ({
      ...prev,
      [channel === 'phone' ? 'phone_otp_verified' : 'email_otp_verified']: value,
    }));
  };

  const getOtpValue = (channel: OtpChannel) => (channel === 'phone' ? form.phone_otp : form.email_otp);
  const isOtpVerified = (channel: OtpChannel) =>
    channel === 'phone' ? form.phone_otp_verified : form.email_otp_verified;

  const updateText = (name: TextFieldName) => (event: ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => {
      const next = { ...prev, [name]: event.target.value };

      if (name === 'phone') {
        next.phone_otp = '';
        next.phone_otp_verified = false;
      }

      if (name === 'email') {
        next.email_otp = '';
        next.email_otp_verified = false;
      }

      return next;
    });

    if (name === 'phone' || name === 'email') {
      setOtpIssued((prev) => ({ ...prev, [name]: false }));
      setChannelStatus(name, null);
    }
  };

  const buildPhoneOtpPayload = () => {
    const normalizedPhone = normalizeHongKongMobile(form.phone);

    if (!normalizedPhone) {
      setChannelStatus('phone', {
        tone: 'error',
        message: tr(
          'pbl.lawyerRegistration.otp.smsPhoneInvalid',
          'SMS phone number must be 8 digits and start with 4, 5, 6, 7, 8 or 9.'
        ),
      });
      return null;
    }

    return {
      channel: 'phone' as const,
      ...normalizedPhone,
    };
  };

  const buildEmailOtpPayload = () => {
    const email = form.email.trim();

    if (!emailPattern.test(email)) {
      setChannelStatus('email', {
        tone: 'error',
        message: tr(
          'pbl.lawyerRegistration.otp.emailInvalidBeforeOtp',
          'Please enter a valid email address before requesting an OTP.'
        ),
      });
      return null;
    }

    return {
      channel: 'email' as const,
      email,
    };
  };

  const issueOtp = async (channel: OtpChannel) => {
    if (isOtpRequestCoolingDown(channel) || issuingOtp === channel) return;

    const payload = channel === 'phone' ? buildPhoneOtpPayload() : buildEmailOtpPayload();
    if (!payload) return;

    setIssuingOtp(channel);

    try {
      const response = await issuePblCitizenOtp(payload);
      const issueResult = response.data.data;
      const issueFailed =
        response.data.success === false ||
        (issueResult?.sent === false && !issueResult?.bypassed && !issueResult?.bypass_code);

      if (issueFailed) {
        throw new Error(
          response.data.message ||
            tr(
              channel === 'phone'
                ? 'pbl.lawyerRegistration.otp.issueSmsOtpFailed'
                : 'pbl.lawyerRegistration.otp.issueEmailOtpFailed',
              `Failed to issue ${getOtpLabel(channel, tr)}.`
            )
        );
      }

      updateOtpValue(
        channel,
        issueResult?.bypass_code || (process.env.NODE_ENV !== 'production' ? OC_CITIZEN_TEST_STATION_OTP : '')
      );
      updateOtpVerified(channel, false);
      setOtpIssued((prev) => ({ ...prev, [channel]: true }));
      startOtpRequestCooldown(channel, issueResult?.cooldown_seconds);
      setChannelStatus(channel, {
        tone: 'info',
        message:
          response.data.message ||
          tr(
            channel === 'phone' ? 'pbl.lawyerRegistration.otp.smsOtpSent' : 'pbl.lawyerRegistration.otp.emailOtpSent',
            `${getOtpLabel(channel, tr)} sent. Please enter the OTP to verify.`
          ),
      });
    } catch (error: any) {
      const message = getApiErrorMessage(
        error,
        tr(
          channel === 'phone'
            ? 'pbl.lawyerRegistration.otp.issueSmsOtpFailed'
            : 'pbl.lawyerRegistration.otp.issueEmailOtpFailed',
          `Failed to issue ${getOtpLabel(channel, tr)}.`
        )
      );
      setChannelStatus(channel, { tone: 'error', message });
      errorHandler(message);
    } finally {
      setIssuingOtp(null);
    }
  };

  const verifyOtp = async (channel: OtpChannel) => {
    const otp = getOtpValue(channel).trim();

    if (!otp) {
      setChannelStatus(channel, {
        tone: 'error',
        message: tr(
          channel === 'phone' ? 'pbl.lawyerRegistration.otp.smsOtpRequired' : 'pbl.lawyerRegistration.otp.emailOtpRequired',
          `${getOtpLabel(channel, tr)} is required.`
        ),
      });
      return;
    }

    const contactPayload = channel === 'phone' ? buildPhoneOtpPayload() : buildEmailOtpPayload();
    if (!contactPayload) return;

    setVerifyingOtp(channel);

    try {
      const response = await verifyPblCitizenOtp({
        ...contactPayload,
        otp,
      });

      if (response.data.success === false || response.data.data?.verified === false) {
        throw new Error(
          response.data.message ||
            tr(
              channel === 'phone'
                ? 'pbl.lawyerRegistration.otp.verifySmsOtpFailed'
                : 'pbl.lawyerRegistration.otp.verifyEmailOtpFailed',
              `Failed to verify ${getOtpLabel(channel, tr)}.`
            )
        );
      }

      updateOtpVerified(channel, true);
      setOtpIssued((prev) => ({ ...prev, [channel]: true }));
      setChannelStatus(channel, {
        tone: 'success',
        message: tr(
          channel === 'phone'
            ? 'pbl.lawyerRegistration.otp.smsOtpVerified'
            : 'pbl.lawyerRegistration.otp.emailOtpVerified',
          `${getOtpLabel(channel, tr)} verified.`
        ),
      });
    } catch (error: any) {
      const message = getApiErrorMessage(
        error,
        tr(
          channel === 'phone'
            ? 'pbl.lawyerRegistration.otp.verifySmsOtpFailed'
            : 'pbl.lawyerRegistration.otp.verifyEmailOtpFailed',
          `Failed to verify ${getOtpLabel(channel, tr)}.`
        )
      );
      updateOtpVerified(channel, false);
      setChannelStatus(channel, { tone: 'error', message });
      errorHandler(message);
    } finally {
      setVerifyingOtp(null);
    }
  };

  const handleOtpChange = (channel: OtpChannel, event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    updateOtpValue(channel, event.target.value);

    if (isOtpVerified(channel)) {
      updateOtpVerified(channel, false);
    }

    setChannelStatus(channel, null);
  };

  const renderOtpInput = (channel: OtpChannel) => {
    const otpLabel = getOtpLabel(channel, tr);
    const verified = isOtpVerified(channel);

    if (!otpIssued[channel] && !getOtpValue(channel) && !verified) {
      return null;
    }

    return (
      <>
        <OtpFieldRow isMobile={isMobile}>
          <TextField
            label={`${otpLabel} *`}
            value={getOtpValue(channel)}
            onChange={(event) => handleOtpChange(channel, event)}
            variant="standard"
            fullWidth
          />
          <Button
            type="button"
            size="md"
            variant={verified ? 'primary' : 'primary-invert'}
            rounded={false}
            disabled={verified || verifyingOtp === channel}
            onClick={() => verifyOtp(channel)}
          >
            {verified
              ? tr('pbl.lawyerRegistration.otp.verified', 'Verified')
              : verifyingOtp === channel
              ? tr('pbl.lawyerRegistration.otp.verifying', 'Verifying...')
              : tr('pbl.citizenAuthorizationCaseCreate.shared.otp.verifyOtp', 'Verify OTP')}
          </Button>
        </OtpFieldRow>
        {otpStatus[channel] && (
          <OtpStatusText tone={otpStatus[channel]!.tone}>{otpStatus[channel]!.message}</OtpStatusText>
        )}
      </>
    );
  };

  const updateDeclaration = (_event: ChangeEvent<HTMLInputElement>, checked: boolean) => {
    setForm((prev) => ({ ...prev, information_true_declared: checked }));
  };

  const openSignatureModal = (role: OcCitizenSignatureRole) => {
    setActiveSignatureRole(role);
  };

  const closeSignatureModal = () => {
    setActiveSignatureRole(null);
  };

  const saveSignature = async (dataUrl: string) => {
    if (!activeSignatureRole) return;

    try {
      const file = dataUrlToFile(dataUrl, `oc-citizen-${activeSignatureRole}-signature.png`);
      const response = await uploadPblFile(createPblUploadFormData(file, 'citizen_authorization_signature'));
      const fileId = getUploadedPblFileId(response.data.data);

      if (response.data.success === false || typeof fileId !== 'number') {
        throw new Error(
          tr('pbl.citizenAuthorizationCaseCreate.shared.messages.signatureUploadFailed', 'Failed to upload signature.')
        );
      }

      setForm((prev) =>
        signOcCitizenAuthorization(prev, activeSignatureRole, {
          dataUrl,
          fileId,
          signedAt: formatSignatureDate(new Date()),
        })
      );
    } catch (error: any) {
      errorHandler(
        error.response?.data?.message ||
          error.message ||
          tr('pbl.citizenAuthorizationCaseCreate.shared.messages.signatureUploadFailed', 'Failed to upload signature.')
      );
      throw error;
    }
  };

  const openTermsModal = (key: keyof TermsModalOpenState) => {
    setTermsOpen((prev) => ({ ...prev, [key]: true }));
  };

  const openConsentModal = (name: ReadOnlyConsentName) => {
    if (name === 'authorization') {
      setAuthorizationOpen(true);
      return;
    }

    openTermsModal(name);
  };

  const handleReadOnlyConsentChange = (name: ReadOnlyConsentName) => {
    openConsentModal(name);
  };

  const closeTermsModal = (key: string) => {
    setTermsOpen((prev) => ({ ...prev, [key]: false }));
  };

  const acceptTermsModal = (key: string, current: boolean) => {
    if ((key === 'infoCollect' || key === 'terms') && current === false) {
      const field = consentFieldByTermsModalKey[key];
      setForm((prev) => ({ ...prev, [field]: true }));
    }

    closeTermsModal(key);
  };

  const acceptAuthorization = () => {
    setForm((prev) => ({ ...prev, authorisation_agreed: true }));
    setAuthorizationOpen(false);
  };

  const resetForm = () => {
    setForm(applyPblOcContextToAuthorizationForm(initialOcCitizenAuthorizationForm, ocContext));
    setAccountSetupNotification(getDefaultAccountSetupNotification(tr));
    setOtpIssued({ phone: false, email: false });
    setOtpStatus({ phone: null, email: null });
    setIssuingOtp(null);
    setVerifyingOtp(null);
    setOtpCooldownSeconds({ phone: 0, email: 0 });
    closeSignatureModal();
  };

  const createEnquirerAccount = async () => {
    if (!isComplete || isSubmitting) return;

    setIsSubmitting(true);

    try {
      const response = await createPblCitizenAuthorization(buildOcCitizenAuthorizationPayload(form));

      if (response.data.success === false) {
        errorHandler(
          response.data.message ||
            tr(
              'pbl.citizenAuthorizationCaseCreate.accountRegistration.messages.createFailed',
              'Failed to create enquirer account.'
            )
        );
        return;
      }

      const nextAccountSetupNotification = getAccountSetupNotificationState(response.data.data, tr);
      resetForm();
      setAccountSetupNotification(nextAccountSetupNotification);
      setSuccessOpen(true);
    } catch (error: any) {
      errorHandler(
        error.response?.data?.message ||
          error.response?.data?.data ||
          tr(
            'pbl.citizenAuthorizationCaseCreate.accountRegistration.messages.createFailed',
            'Failed to create enquirer account.'
          )
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PageShell>
      <FormCard isTablet={isTablet}>
        <SectionTitle>
          {tr(
            'pbl.citizenAuthorizationCaseCreate.shared.sections.districtOfficeStaff',
            'District Office & Staff Information'
          )}
        </SectionTitle>
        <InfoGrid>
          <InfoLabel>{tr('pbl.citizenAuthorizationCaseCreate.shared.officeFields.legcoOffice', "LegCo Members' Office")}</InfoLabel>
          <InfoValue>{form.oc_office_name}</InfoValue>
          <InfoLabel>
            {tr(
              'pbl.citizenAuthorizationCaseCreate.shared.officeFields.assistingStaffName',
              "Assisting Staff Name (LegCo Members' Office)"
            )}
          </InfoLabel>
          <InfoValue>{form.assisting_staff_name}</InfoValue>
          <InfoLabel>{tr('pbl.citizenAuthorizationCaseCreate.shared.officeFields.officeContact', 'Office Contact')}</InfoLabel>
          <InfoValue>{form.office_contact}</InfoValue>
        </InfoGrid>

        <SectionTitle>
          {tr('pbl.citizenAuthorizationCaseCreate.shared.sections.enquirerInformation', "Enquirer's Information")}
        </SectionTitle>
        <FieldStack>
          <TextField
            label={tr('pbl.citizenAuthorizationCaseCreate.shared.enquirerFields.surnameEnglish', 'Surname (English) *')}
            value={form.citizen_surname_en}
            onChange={updateText('citizen_surname_en')}
            variant="standard"
            fullWidth
          />
          <TextField
            label={tr('pbl.citizenAuthorizationCaseCreate.shared.enquirerFields.givenNameEnglish', 'Given Name (English) *')}
            value={form.citizen_given_name_en}
            onChange={updateText('citizen_given_name_en')}
            variant="standard"
            fullWidth
          />
          <TextField
            label={tr('pbl.citizenAuthorizationCaseCreate.shared.enquirerFields.surnameChinese', 'Surname (Chinese)')}
            value={form.citizen_surname_zh}
            onChange={updateText('citizen_surname_zh')}
            variant="standard"
            fullWidth
          />
          <TextField
            label={tr('pbl.citizenAuthorizationCaseCreate.shared.enquirerFields.givenNameChinese', 'Given Name (Chinese)')}
            value={form.citizen_given_name_zh}
            onChange={updateText('citizen_given_name_zh')}
            variant="standard"
            fullWidth
          />
          <TextField
            label={tr(
              'pbl.citizenAuthorizationCaseCreate.shared.enquirerFields.identityPartial',
              'HKID / Passport No.:(partial - first 4 digits only) *'
            )}
            value={form.identity_partial}
            onChange={updateText('identity_partial')}
            variant="standard"
            fullWidth
          />
          <OtpControlGroup>
            <OtpFieldRow isMobile={isMobile}>
              <TextField
                label={tr('pbl.citizenAuthorizationCaseCreate.shared.enquirerFields.phoneNumber', 'Phone Number (required when email is blank)')}
                value={form.phone}
                onChange={updateText('phone')}
                variant="standard"
                fullWidth
              />
              <Button
                type="button"
                size="md"
                variant="primary-invert"
                rounded={false}
                disabled={!form.phone.trim() || issuingOtp === 'phone' || isOtpRequestCoolingDown('phone')}
                onClick={() => issueOtp('phone')}
              >
                {getOtpRequestButtonLabel('phone')}
              </Button>
            </OtpFieldRow>
            {otpStatus.phone && !otpIssued.phone && (
              <OtpStatusText tone={otpStatus.phone.tone}>{otpStatus.phone.message}</OtpStatusText>
            )}
            {renderOtpInput('phone')}
          </OtpControlGroup>
          <OtpControlGroup>
            <EmailFieldRow data-testid="oc-citizen-email-field-row">
              <TextField
                label={tr('pbl.citizenAuthorizationCaseCreate.shared.enquirerFields.emailAddress', 'Email Address (required when phone is blank)')}
                value={form.email}
                onChange={updateText('email')}
                variant="standard"
                fullWidth
              />
            </EmailFieldRow>
            {form.email.trim() ? (
              <EmailOtpActionRow>
                <Button
                  type="button"
                  size="md"
                  variant="primary-invert"
                  rounded={false}
                  disabled={issuingOtp === 'email' || isOtpRequestCoolingDown('email')}
                  onClick={() => issueOtp('email')}
                >
                  {getOtpRequestButtonLabel('email')}
                </Button>
              </EmailOtpActionRow>
            ) : null}
            {otpStatus.email && !otpIssued.email && (
              <OtpStatusText tone={otpStatus.email.tone}>{otpStatus.email.message}</OtpStatusText>
            )}
            {renderOtpInput('email')}
          </OtpControlGroup>
          <TextField
            label={tr(
              'pbl.citizenAuthorizationCaseCreate.shared.enquirerFields.residentialAddress',
              'Residential Address *'
            )}
            value={form.residential_address}
            onChange={updateText('residential_address')}
            variant="standard"
            fullWidth
          />
        </FieldStack>

        <SectionTitle>
          {tr(
            'pbl.citizenAuthorizationCaseCreate.accountRegistration.declarationConsent.title',
            'Declaration and Consent'
          )}
        </SectionTitle>
        <ConsentList>
          {readOnlyConsentLabels.map((item) => {
            const linkText = tr(item.linkKey, item.linkDefault);
            const suffix = item.suffixKey ? tr(item.suffixKey, item.suffixDefault || '') : '';

            return (
            <FormControlLabel
              key={item.name}
              control={
                <Checkbox
                  checked={form[item.field]}
                  onChange={() => handleReadOnlyConsentChange(item.name)}
                  inputProps={{ 'aria-label': linkText }}
                />
              }
              label={
                <ConsentText>
                  {tr(
                    'pbl.citizenAuthorizationCaseCreate.accountRegistration.declarationConsent.readAndAgreePrefix',
                    'I confirm that I have read and agree to the'
                  )}{' '}
                  <button
                    type="button"
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      openConsentModal(item.name);
                    }}
                  >
                    {linkText}
                  </button>
                  {suffix ? ` ${suffix}` : null}
                </ConsentText>
              }
            />
          );
          })}
          <FormControlLabel
            control={<Checkbox checked={form.information_true_declared} onChange={updateDeclaration} />}
            label={
              <ConsentText>
                {tr(
                  'pbl.citizenAuthorizationCaseCreate.accountRegistration.declarationConsent.truthDeclaration',
                  'I declare that all information provided is true and complete.'
                )}
              </ConsentText>
            }
          />
        </ConsentList>

        <SectionTitle>{tr('pbl.citizenAuthorizationCaseCreate.shared.sections.signature', 'Signature')}</SectionTitle>
        <SignatureBlock>
          <SignatureItem>
            <SignatureRow isMobile={isMobile}>
              <Text as="span" margin={0}>
                {tr('pbl.citizenAuthorizationCaseCreate.accountRegistration.signature.enquirer', 'Signature of Enquirer :')}
              </Text>
              {form.enquirer_signature ? (
                <SignedBadge>{tr('pbl.citizenAuthorizationCaseCreate.shared.actions.signed', 'Signed')}</SignedBadge>
              ) : (
                <Button
                  type="button"
                  size="md"
                  variant="primary"
                  rounded={false}
                  onClick={() => openSignatureModal('enquirer')}
                >
                  {tr('pbl.citizenAuthorizationCaseCreate.shared.actions.sign', 'Sign')}
                </Button>
              )}
              <DateText>
                {tr('pbl.citizenAuthorizationCaseCreate.accountRegistration.signature.date', 'Date :')}{' '}
                {form.enquirer_signed_at ? formatPblDisplayDate(form.enquirer_signed_at) : ''}
              </DateText>
            </SignatureRow>
            {form.enquirer_signature ? (
              <SignaturePreview>
                <SignaturePreviewImage
                  src={form.enquirer_signature}
                  alt={tr(
                    'pbl.citizenAuthorizationCaseCreate.accountRegistration.signature.enquirerAlt',
                    'Enquirer signature preview'
                  )}
                />
                <SignaturePreviewAction type="button" onClick={() => openSignatureModal('enquirer')}>
                  {tr('pbl.citizenAuthorizationCaseCreate.shared.actions.resign', 'Re-sign')}
                </SignaturePreviewAction>
              </SignaturePreview>
            ) : null}
          </SignatureItem>

          <SignatureItem>
            <SignatureRow isMobile={isMobile}>
              <Text as="span" margin={0}>
                {tr(
                  'pbl.citizenAuthorizationCaseCreate.accountRegistration.signature.witness',
                  'Witness (LegCo Office Staff) :'
                )}
              </Text>
              {form.witness_staff_signature ? (
                <SignedBadge>{tr('pbl.citizenAuthorizationCaseCreate.shared.actions.signed', 'Signed')}</SignedBadge>
              ) : (
                <Button
                  type="button"
                  size="md"
                  variant="primary"
                  rounded={false}
                  onClick={() => openSignatureModal('witness')}
                >
                  {tr('pbl.citizenAuthorizationCaseCreate.shared.actions.sign', 'Sign')}
                </Button>
              )}
              <DateText>
                {tr('pbl.citizenAuthorizationCaseCreate.accountRegistration.signature.date', 'Date :')}{' '}
                {form.witness_staff_signed_at ? formatPblDisplayDate(form.witness_staff_signed_at) : ''}
              </DateText>
            </SignatureRow>
            {form.witness_staff_signature ? (
              <SignaturePreview>
                <SignaturePreviewImage
                  src={form.witness_staff_signature}
                  alt={tr(
                    'pbl.citizenAuthorizationCaseCreate.accountRegistration.signature.witnessAlt',
                    'Witness signature preview'
                  )}
                />
                <SignaturePreviewAction type="button" onClick={() => openSignatureModal('witness')}>
                  {tr('pbl.citizenAuthorizationCaseCreate.shared.actions.resign', 'Re-sign')}
                </SignaturePreviewAction>
              </SignaturePreview>
            ) : null}
          </SignatureItem>
        </SignatureBlock>

        <ActionRow isMobile={isMobile}>
          <Button
            type="button"
            size="lg"
            variant="primary"
            rounded={false}
            disabled={!isComplete || isSubmitting}
            onClick={createEnquirerAccount}
          >
            {isSubmitting
              ? tr('pbl.citizenAuthorizationCaseCreate.accountRegistration.actions.creating', 'Creating...')
              : tr(
                  'pbl.citizenAuthorizationCaseCreate.accountRegistration.actions.createEnquirerAccount',
                  'Create Enquirer Account'
                )}
          </Button>
          <Button type="button" size="lg" variant="primary-invert" rounded={false} onClick={resetForm}>
            {tr('pbl.citizenAuthorizationCaseCreate.shared.actions.cancel', 'Cancel')}
          </Button>
        </ActionRow>
      </FormCard>

      <Modal open={successOpen} onClose={() => setSuccessOpen(false)} aria-labelledby="oc-register-success-title">
        <ModalSurface>
          <ModalTitle id="oc-register-success-title">
            {tr(
              'pbl.citizenAuthorizationCaseCreate.accountRegistration.successModal.title',
              'Sub-account Created Successfully'
            )}
          </ModalTitle>
          <ModalText>
            {tr(
              'pbl.citizenAuthorizationCaseCreate.accountRegistration.successModal.accountCreated',
              'The citizen account has been created successfully.'
            )}
          </ModalText>
          {accountSetupNotification.showSentEmailInstruction && (
            <ModalText>
              {tr(
                'pbl.citizenAuthorizationCaseCreate.accountRegistration.successModal.emailInstruction',
                'The system has sent an account setup email. The citizen must open the secure link in that email and set a first password before signing in.'
              )}
            </ModalText>
          )}
          {accountSetupNotification.notice && <ModalText>{accountSetupNotification.notice}</ModalText>}
          <ModalText>{accountSetupNotification.followUpInstruction}</ModalText>
          <ModalAction>
            <Button type="button" size="lg" variant="primary" rounded={false} onClick={() => setSuccessOpen(false)}>
              {tr('pbl.citizenAuthorizationCaseCreate.shared.actions.done', 'Done')}
            </Button>
          </ModalAction>
        </ModalSurface>
      </Modal>

      <TermsModal open={termsOpen} handleClose={closeTermsModal} handleChange={acceptTermsModal} />

      <SignatureModal
        isOpen={activeSignatureRole !== null}
        onClose={closeSignatureModal}
        onConfirm={saveSignature}
        onEmptySignature={() =>
          errorHandler(
            tr('pbl.citizenAuthorizationCaseCreate.shared.messages.signatureRequired', 'Please provide a signature before confirming.')
          )
        }
        title={
          activeSignatureRole === 'witness'
            ? tr(
                'pbl.citizenAuthorizationCaseCreate.accountRegistration.signature.witnessModalTitle',
                'Witness (LegCo Office Staff) signature'
              )
            : tr(
                'pbl.citizenAuthorizationCaseCreate.accountRegistration.signature.enquirerModalTitle',
                'Signature of Enquirer'
              )
        }
      />

      <Modal
        open={authorizationOpen}
        onClose={() => setAuthorizationOpen(false)}
        aria-labelledby="oc-authorization-title"
      >
        <AuthorizationModalSurface>
          <AuthorizationModalTitle id="oc-authorization-title">
            {tr('pbl.citizenAuthorizationCaseCreate.accountRegistration.authorizationModal.title', 'Authorization')}
          </AuthorizationModalTitle>
          <AuthorizationModalText>
            {tr(
              'pbl.citizenAuthorizationCaseCreate.accountRegistration.authorizationModal.intro',
              'I, the undersigned, authorise the above {{officeName}} LegCo Office to:',
              { officeName: form.oc_office_name }
            )}
          </AuthorizationModalText>
          <AuthorizationModalText>
            {tr(
              'pbl.citizenAuthorizationCaseCreate.accountRegistration.authorizationModal.permissions',
              'Set up a sub-account on the eBRAM DMP platform on my behalf; Assist me in seeking legal assistance under the Scheme including putting my personal data and enquiry details where necessary to the Scheme platform and submitting the FreelegalLink Online Enquiry Form to the selected pro bono lawyer to handle the enquiry; Access my sub-account and all information contained in the sub-account including my personal data and enquiry details to assist me in following up the progress of my enquiry; Assist me in communicating with eBRAM and pro bono lawyers regarding my enquiry.'
            )}
          </AuthorizationModalText>
          <AuthorizationModalAction>
            <Button type="button" size="lg" variant="primary" rounded={false} onClick={acceptAuthorization}>
              {tr('pbl.citizenAuthorizationCaseCreate.shared.actions.agree', 'Agree')}
            </Button>
            <Button
              type="button"
              size="lg"
              variant="primary-invert"
              rounded={false}
              onClick={() => setAuthorizationOpen(false)}
            >
              {tr('pbl.citizenAuthorizationCaseCreate.shared.actions.back', 'Back')}
            </Button>
          </AuthorizationModalAction>
        </AuthorizationModalSurface>
      </Modal>
    </PageShell>
  );
};

export default OcCitizenRegistration;
